import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { db } from "../firebase";
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  where,
  limit,
  documentId,
} from "firebase/firestore";
import { motion } from "framer-motion";
import { Pin } from "lucide-react";

import Header from "../components/Header";
import ProfileIcon from "../components/ProfileIcon";
import Button from "../components/Button";
import MemoryNoteCard from "../components/MemoryNoteCard";
import PinnedPhotosCarousel from "../components/PinnedPhotosCarousel";
import PinPhotosSheet from "../components/PinPhotosSheet";
import PinNoteSheet from "../components/PinNoteSheet";

import { useAdminMode } from "../context/AdminModeContext";
import { useCurrentMemorial } from "../context/CurrentMemorialContext";
import { useMemorial } from "../hooks/useMemorial";
import {
  setPinnedImages as savePinnedImages,
  setPinnedNote as savePinnedNote,
} from "../services/memorialService";
import { formatDate } from "../utils/textHelpers";

/**
 * Born / deceased glyphs — intentionally *not* lucide icons. These
 * reuse the original hand-drawn lotus + cross marks from the app's
 * earliest design so the hero keeps its softer, less "system-icon"
 * feel. Color is driven by the `fill` prop (matches surrounding text).
 */
const BirthIcon = ({ size = 18, color = "#8E7C69" }) => (
  <svg
    width={size * (22 / 16)}
    height={size}
    viewBox="0 0 22 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    style={{ display: "block" }}
  >
    <path
      d="M21.8043 8.14818C21.6153 7.81895 21.3008 7.57883 20.9315 7.48182C20.2682 7.31253 19.5829 7.24447 18.8989 7.28C19.2657 5.47545 18.9906 4.04818 18.7101 3.20181C18.4784 2.48145 17.7328 2.05337 16.9874 2.21273C16.0545 2.41875 15.1674 2.79216 14.3699 3.31454C13.7859 2.13149 12.9355 1.09731 11.8844 0.291817C11.3612 -0.0972725 10.6419 -0.0972725 10.1187 0.291817C9.06739 1.09707 8.21698 2.13131 7.6332 3.31454C6.83572 2.79216 5.94864 2.41875 5.01572 2.21273C4.27031 2.05337 3.52474 2.48145 3.29304 3.20181C3.018 4.04818 2.74295 5.47455 3.10417 7.28C2.42025 7.24447 1.73485 7.31253 1.07161 7.48182C0.702297 7.57883 0.387801 7.81895 0.198809 8.14818C0.00102958 8.48544 -0.051822 8.88736 0.0521186 9.26364C0.362923 10.4064 1.31732 12.58 4.20801 14.2927C7.0987 16.0055 9.63826 16 11.0061 16C12.374 16 14.9182 16 17.7905 14.2927C20.6812 12.58 21.6356 10.4064 21.9464 9.26364C22.0517 8.88796 22.0005 8.48608 21.8043 8.14818ZM4.68842 3.64909C4.69069 3.63972 4.69994 3.63375 4.70951 3.63545C5.56258 3.82654 6.36762 4.18747 7.07578 4.69637C6.75334 5.74233 6.59321 6.83084 6.60088 7.92454C6.60088 9.62818 6.94652 11.0155 7.43609 12.1318C6.74009 11.4411 6.13787 10.6633 5.64465 9.81818C3.99256 6.96546 4.33637 4.72727 4.68842 3.64909ZM4.96346 13.0445C2.51834 11.5973 1.7253 9.82272 1.46675 8.88182C2.14136 8.71713 2.84139 8.68104 3.52958 8.77546C3.75933 9.38571 4.03993 9.97593 4.36845 10.54C5.17512 11.919 6.23192 13.1383 7.4856 14.1364C6.59909 13.8836 5.7499 13.5166 4.9598 13.0445H4.96346ZM11.0016 14.4C10.1462 13.7691 8.06777 11.8336 8.06777 7.92454C8.06777 4.06363 10.1196 2.11272 11.0016 1.45454C11.8835 2.11454 13.9353 4.06545 13.9353 7.92636C13.9353 11.8336 11.8569 13.7691 11.0016 14.4ZM14.9273 4.69637C15.6356 4.18779 16.4406 3.82717 17.2936 3.63636C17.3032 3.63465 17.3124 3.64063 17.3147 3.65C17.6667 4.72727 18.0105 6.96546 16.3585 9.81818C15.8657 10.6644 15.2634 11.4431 14.567 12.1345C15.0566 11.02 15.4022 9.6309 15.4022 7.92727C15.4102 6.83264 15.25 5.74321 14.9273 4.69637ZM20.5363 8.88364C20.2824 9.81545 19.4912 11.5936 17.0442 13.0445C16.2541 13.5162 15.4049 13.883 14.5184 14.1355C15.7721 13.1374 16.8289 11.9181 17.6356 10.5391C17.9641 9.97501 18.2447 9.38479 18.4745 8.77455C19.1625 8.68055 19.8622 8.71817 20.5363 8.88364Z"
      fill={color}
    />
  </svg>
);

const DeathIcon = ({ size = 14, color = "#8E7C69" }) => (
  <svg
    width={size * (12 / 16)}
    height={size}
    viewBox="0 0 12 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    style={{ display: "block" }}
  >
    <path
      d="M6 1V15M1 5.375H11"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="bevel"
    />
  </svg>
);

export default function Memorial() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { adminMode } = useAdminMode();
  const { setCurrent } = useCurrentMemorial();

  const { memorial, isOwner, isLoading } = useMemorial(slug);

  // Display data — available to all visitors
  const [homeImages, setHomeImages] = useState([]);
  const [latestOwnerNote, setLatestOwnerNote] = useState(null);
  const [pinnedImages, setPinnedImagesState] = useState([]);
  const [pinnedNote, setPinnedNote] = useState(null);

  // Admin-only picker data — gated, only fetched when owner is in admin mode
  const [availableOwnerImages, setAvailableOwnerImages] = useState([]);
  const [availableNotes, setAvailableNotes] = useState([]);

  // Sheet visibility
  const [isPinning, setIsPinning] = useState(false);
  const [isChoosingNote, setIsChoosingNote] = useState(false);

  // Sync current memorial into context so Navbar and FAB always have the slug
  useEffect(() => {
    if (memorial) {
      setCurrent({ slug, memorialId: memorial.id });
    } else if (memorial === null) {
      setCurrent({ slug: null, memorialId: null });
    }
  }, [memorial, slug, setCurrent]);

  // Public subscriptions: home-page images and the latest owner note
  useEffect(() => {
    if (!memorial?.id) return;
    const mref = collection(db, "memorials", memorial.id, "memories");

    const unsubImages = onSnapshot(
      query(
        mref,
        where("type", "==", "image"),
        where("uploadedByOwner", "==", true),
        orderBy("createdAt", "desc"),
        limit(10)
      ),
      (snap) => setHomeImages(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    const unsubNote = onSnapshot(
      query(
        mref,
        where("type", "==", "note"),
        where("uploadedByOwner", "==", true),
        orderBy("createdAt", "desc"),
        limit(1)
      ),
      (snap) =>
        setLatestOwnerNote(
          snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() }
        )
    );

    return () => {
      unsubImages();
      unsubNote();
    };
  }, [memorial?.id]);

  // Pinned images subscription
  useEffect(() => {
    if (!memorial?.id) return;
    const ids = Array.isArray(memorial.pinnedImageIds) ? memorial.pinnedImageIds : [];
    const unique = Array.from(new Set(ids.filter(Boolean))).slice(0, 3);

    if (ids.length === 0) {
      setPinnedImagesState([]);
      return;
    }

    const unsub = onSnapshot(
      query(
        collection(db, "memorials", memorial.id, "memories"),
        where(documentId(), "in", ids)
      ),
      (snap) => {
        const map = new Map(snap.docs.map((d) => [d.id, { id: d.id, ...d.data() }]));
        setPinnedImagesState(unique.map((id) => map.get(id)).filter(Boolean));
      }
    );
    return () => unsub();
  }, [memorial?.id, memorial?.pinnedImageIds]);

  // Pinned note subscription
  useEffect(() => {
    if (!memorial?.id) return;
    const nid = memorial.pinnedNoteId;
    if (!nid) {
      setPinnedNote(null);
      return;
    }
    const unsub = onSnapshot(
      doc(db, "memorials", memorial.id, "memories", nid),
      (ds) => {
        if (ds.exists()) setPinnedNote({ id: ds.id, ...ds.data() });
      }
    );
    return () => unsub();
  }, [memorial?.id, memorial?.pinnedNoteId]);

  // Admin-only: load picker data only when the owner has admin mode active.
  // Avoids opening two full-collection subscriptions for every anonymous visitor.
  useEffect(() => {
    if (!memorial?.id || !isOwner || !adminMode) return;
    const mref = collection(db, "memorials", memorial.id, "memories");

    const unsubOwnerImages = onSnapshot(
      query(mref, where("type", "==", "image"), orderBy("createdAt", "desc")),
      (snap) =>
        setAvailableOwnerImages(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    const unsubNotesList = onSnapshot(
      query(mref, where("type", "==", "note"), orderBy("createdAt", "desc")),
      (snap) => setAvailableNotes(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    return () => {
      unsubOwnerImages();
      unsubNotesList();
    };
  }, [memorial?.id, isOwner, adminMode]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[var(--color-bg)]">
        <div className="w-8 h-8 border-2 border-[#56371F] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (memorial === null) {
    return (
      <div className="min-h-screen bg-[var(--color-bg)] flex items-center justify-center px-6">
        <div className="w-full max-w-md bg-white rounded-[20px] shadow p-8 text-center">
          <h1 className="text-[22px] font-semibold text-[#44231E] mb-3">Memorial not found</h1>
          <p className="text-[14px] text-[#6F6F6F] mb-6">
            This memorial page URL or code does not exist.
          </p>
          <Button onClick={() => navigate("/")}>Go back</Button>
        </div>
      </div>
    );
  }

  const displayImages = pinnedImages.length > 0 ? pinnedImages : homeImages.slice(0, 3);
  const note = pinnedNote || latestOwnerNote;
  const noteDate = formatDate(note?.createdAt);

  const handleSavePinnedPhotos = async (selectedIds) => {
    await savePinnedImages(memorial.id, selectedIds);
    setIsPinning(false);
  };

  const handleSavePinnedNote = async (selection) => {
    if (selection.type === "new") {
      const created = await addDoc(
        collection(db, "memorials", memorial.id, "memories"),
        {
          type: "note",
          text: selection.text,
          authorName: selection.authorName,
          uploadedByOwner: true,
          isFeatured: true,
          createdAt: serverTimestamp(),
        }
      );
      await savePinnedNote(memorial.id, created.id);
    } else {
      await savePinnedNote(memorial.id, selection.noteId);
    }
    setIsChoosingNote(false);
  };

  return (
    <div className="min-h-screen overflow-y-auto overflow-x-hidden" style={{ background: "var(--color-bg)" }}>
      <div className="fixed top-0 left-0 right-0 z-50">
        <Header title="Home">
          <button
            onClick={() => navigate(`/m/${slug}/my-uploads`)}
            className="focus:outline-none"
          >
            <ProfileIcon />
          </button>
        </Header>
      </div>

      <motion.div
        className="w-full"
        style={{
          paddingTop: "max(60px, calc(52px + env(safe-area-inset-top, 0px)))",
          paddingBottom: 180,
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.25 }}
      >
        {/* ── Pinned photos carousel ─────────────────────────────── */}
        {displayImages.length > 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 240, damping: 24 }}
          >
            <PinnedPhotosCarousel
              images={displayImages}
              onSlideClick={() => navigate(`/m/${slug}/images`)}
              initialIndex={Math.min(1, displayImages.length - 1)}
            />
          </motion.div>
        ) : (
          <motion.div
            className="mx-6 rounded-[24px] flex flex-col items-center justify-center text-center"
            style={{
              marginTop: 12,
              marginBottom: 24,
              padding: "48px 24px",
              border: "1px dashed rgba(68, 35, 30, 0.22)",
              background: "rgba(255, 255, 255, 0.6)",
              color: "#6A5A45",
              fontFamily: "Poppins",
            }}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Pin size={22} color="#8E7C69" strokeWidth={2} />
            <div className="mt-3 text-[15px] font-medium">No photos pinned yet</div>
            <div className="mt-1 text-[13px]" style={{ color: "#8E7C69" }}>
              Pinned photos appear here as a little gallery.
            </div>
          </motion.div>
        )}

        {adminMode && isOwner && (
          <div className="flex justify-center" style={{ marginTop: 4, marginBottom: 28 }}>
            <Button
              trailingIconName="change"
              onClick={() => setIsPinning(true)}
            >
              Edit pinned photos
            </Button>
          </div>
        )}

        {/* ── Hero: name + dates ─────────────────────────────────── */}
        <motion.div
          className="px-6 flex flex-col items-center text-center"
          style={{ marginTop: displayImages.length ? 16 : 8, marginBottom: 28 }}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08, type: "spring", stiffness: 240, damping: 26 }}
        >
          <h1
            style={{
              color: "#44231E",
              fontFamily: "Poppins",
              fontWeight: 600,
              fontSize: 30,
              lineHeight: 1.15,
              letterSpacing: "-0.02em",
              margin: 0,
            }}
          >
            {memorial.displayName}
          </h1>

          <div
            className="inline-flex items-center"
            style={{
              marginTop: 14,
              gap: 12,
              padding: "10px 18px",
              background: "rgba(255, 255, 255, 0.75)",
              border: "1px solid rgba(68, 35, 30, 0.08)",
              borderRadius: 999,
              boxShadow: "0 2px 8px rgba(64, 42, 20, 0.06)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
            }}
          >
            <span className="inline-flex items-center gap-2">
              <BirthIcon size={14} color="#8E7C69" />
              <span
                style={{
                  color: "#5B4A35",
                  fontFamily: "Poppins",
                  fontSize: 14,
                  fontWeight: 500,
                  lineHeight: 1,
                }}
              >
                {memorial.birthYear || "—"}
              </span>
            </span>
            <span
              aria-hidden
              style={{
                width: 14,
                height: 2,
                borderRadius: 2,
                background: "#B9A68E",
                flexShrink: 0,
              }}
            />
            <span className="inline-flex items-center gap-2">
              <DeathIcon size={12} color="#8E7C69" />
              <span
                style={{
                  color: "#5B4A35",
                  fontFamily: "Poppins",
                  fontSize: 14,
                  fontWeight: 500,
                  lineHeight: 1,
                }}
              >
                {memorial.deathYear || "—"}
              </span>
            </span>
          </div>
        </motion.div>

        {/* ── Pinned memory note ─────────────────────────────────── */}
        <div className="px-6">
          <motion.div
            className="flex items-center gap-2 mb-3 pl-1"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.16 }}
          >
            <Pin size={14} color="#8E7C69" strokeWidth={2} fill="#8E7C69" />
            <span
              style={{
                color: "#8E7C69",
                fontFamily: "Poppins",
                fontSize: 12,
                fontWeight: 500,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              Pinned note
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 240, damping: 28 }}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.995 }}
          >
            <MemoryNoteCard
              date={noteDate}
              body={note?.text || "No memory note pinned yet."}
              author={note?.authorName || ""}
              headerEnd={<Pin size={14} color="#B9A68E" strokeWidth={2} fill="#B9A68E" />}
            >
              {adminMode && isOwner && (
                <div className="mt-3 flex items-center gap-2">
                  <Button
                    trailingIconName="change"
                    onClick={() => setIsChoosingNote(true)}
                  >
                    Change pinned note
                  </Button>
                </div>
              )}
            </MemoryNoteCard>
          </motion.div>
        </div>
      </motion.div>

      {/* ── Admin: pin photos sheet ───────────────────────────────── */}
      {adminMode && isOwner && isPinning && (
        <PinPhotosSheet
          memorialId={memorial.id}
          availableOwnerImages={availableOwnerImages}
          initialSelectedIds={pinnedImages.map((img) => img.id)}
          onSave={handleSavePinnedPhotos}
          onClose={() => setIsPinning(false)}
        />
      )}

      {/* ── Admin: choose pinned note sheet ──────────────────────── */}
      {adminMode && isOwner && isChoosingNote && (
        <PinNoteSheet
          availableNotes={availableNotes}
          initialNoteId={memorial.pinnedNoteId || null}
          onSave={handleSavePinnedNote}
          onClose={() => setIsChoosingNote(false)}
        />
      )}
    </div>
  );
}
